import React, { useState, useContext, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../components/shared/useUser';
import {
  getCashTransactionClient,
  CreateCashDropRequest,
  CashZoneDto,
  TillStateDto,
  formatCashAmount,
} from '../../../app/services/api/cashTransactions';
import useLogError from '../../../hooks/useLogError';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';

interface CashDropModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CashDropModal: React.FC<CashDropModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation('cashReconciliation');
  const { selectedStore } = useContext(UserContext);
  const { logError } = useLogError();
  const cashClient = getCashTransactionClient();

  const [loading, setLoading] = useState(false);
  const [tillStates, setTillStates] = useState<TillStateDto[]>([]);
  const [zones, setZones] = useState<CashZoneDto[]>([]);
  const [selectedTillNumber, setSelectedTillNumber] = useState<string>('');
  const [toZoneId, setToZoneId] = useState<number | ''>('');
  const [amount, setAmount] = useState<string>('');
  const [referenceId, setReferenceId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (open && selectedStore?.storeNumber) {
      loadData();
    }
  }, [open, selectedStore]);

  const loadData = async () => {
    if (!selectedStore?.storeNumber) return;

    try {
      setLoading(true);
      const [tillsData, zonesData] = await Promise.all([
        cashClient.getAllTillStates(selectedStore.storeNumber),
        cashClient.getCashZones(selectedStore.storeNumber),
      ]);

      // Only show open tills
      const openTills = tillsData.filter(t => t.status === 'Open');
      setTillStates(openTills);

      // Show safe and back office zones as drop destinations
      const dropZones = zonesData.filter(z =>
        (z.zoneType === 'Safe' || z.zoneType === 'BackOffice') && z.isActive,
      );
      setZones(dropZones);

    } catch (err) {
      logError(err);
      setError(t('errors.loadDataFailed', 'Failed to load till data'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');

    // Validation
    if (!selectedTillNumber) {
      setError(t('errors.selectTill', 'Please select a till'));
      return;
    }

    if (!toZoneId) {
      setError(t('errors.selectDestination', 'Please select a destination'));
      return;
    }

    const dropAmount = parseFloat(amount);
    if (isNaN(dropAmount) || dropAmount <= 0) {
      setError(t('errors.invalidAmount', 'Please enter a valid amount'));
      return;
    }

    // Check if till has sufficient balance
    const tillState = tillStates.find(t => t.tillNumber === selectedTillNumber);
    if (tillState && dropAmount > tillState.currentBalance) {
      setError(
        t(
          'errors.insufficientBalance',
          'Amount exceeds current till balance of {{balance}}',
          { balance: formatCashAmount(tillState.currentBalance, 'GBP') },
        ),
      );
      return;
    }

    try {
      setLoading(true);

      const request: CreateCashDropRequest = {
        storeNumber: selectedStore?.storeNumber || '',
        fromTillNumber: selectedTillNumber,
        toZoneId: toZoneId as number,
        amount: dropAmount,
        referenceId: referenceId.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      await cashClient.createCashDrop(request);

      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);

    } catch (err) {
      logError(err);
      setError(t('errors.cashDropFailed', 'Failed to process cash drop. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTillNumber('');
    setToZoneId('');
    setAmount('');
    setReferenceId('');
    setNotes('');
    setError('');
    onClose();
  };

  const selectedTillState = tillStates.find(t => t.tillNumber === selectedTillNumber);

  return (
    <>
      <Dialog open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('cashDrop.title', 'Cash Drop')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3}
            sx={{ mt: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {tillStates.length === 0 && !loading && (
              <Alert severity="warning">
                {t('cashDrop.noOpenTills', 'No open tills available for cash drop')}
              </Alert>
            )}

            <TextField
              select
              label={t('cashDrop.fromTill', 'From Till')}
              value={selectedTillNumber}
              onChange={(e) => setSelectedTillNumber(e.target.value)}
              disabled={loading || tillStates.length === 0}
              required
              fullWidth
            >
              {tillStates.map((till) => (
                <MenuItem key={till.tillNumber}
                  value={till.tillNumber}>
                  {till.tillNumber} - {formatCashAmount(till.currentBalance, 'GBP')}
                </MenuItem>
              ))}
            </TextField>

            {selectedTillState && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="textSM"
                  color="text.secondary">
                  {t('cashDrop.currentBalance', 'Current Balance')}:
                </Typography>
                <Typography variant="textLG"
                  fontWeight={700}
                  color="primary">
                  {formatCashAmount(selectedTillState.currentBalance, 'GBP')}
                </Typography>
              </Box>
            )}

            <TextField
              select
              label={t('cashDrop.toLocation', 'To Location')}
              value={toZoneId}
              onChange={(e) => setToZoneId(Number(e.target.value))}
              disabled={loading}
              required
              fullWidth
            >
              {zones.map((zone) => (
                <MenuItem key={zone.id}
                  value={zone.id}>
                  {zone.zoneName}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('cashDrop.amount', 'Amount (£)')}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={loading}
              required
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
            />

            <TextField
              label={t('cashDrop.referenceId', 'Reference ID (Optional)')}
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              disabled={loading}
              fullWidth
              placeholder={t('cashDrop.referenceIdPlaceholder', 'e.g., Bag ID or receipt number')}
            />

            <TextField
              label={t('cashDrop.notes', 'Notes (Optional)')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              fullWidth
              multiline
              rows={2}
              placeholder={t('cashDrop.notesPlaceholder', 'Any additional notes...')}
            />

            <Alert severity="info">
              {t(
                'cashDrop.info',
                'This will record a cash drop event and update the till balance. Ensure cash is physically secured.',
              )}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}
            disabled={loading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="primary"
            disabled={loading || tillStates.length === 0}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {t('cashDrop.recordDrop', 'Record Cash Drop')}
          </Button>
        </DialogActions>
      </Dialog>

      <MessagesSnackbar
        open={showSuccess}
        message={t('cashDrop.success', 'Cash drop recorded successfully')}
        severity="success"
        duration={3000}
        onClose={() => setShowSuccess(false)}
      />
    </>
  );
};

export default CashDropModal;
